import * as React from 'react';
import {
  TextField,
  type TextFieldProps,
  Select as MuiSelect,
  type SelectProps,
  FormHelperText,
  FormControl,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';

export type SelectOption = {
  value: string | number;
  label?: string | number;
  disabled?: boolean;
  shouldShow?: boolean;
  children?: SelectOption[];
};

export type CustomSelectProps = TextFieldProps & {
  options: SelectOption[];
  isGroup?: boolean;
  clearable?: boolean;
};

export type RawSelectProps = SelectProps & {
  placeholder: string;
  options: SelectOption[];
  helperText: React.ReactNode;
};

export const EmptySelection: SelectOption = {
  value: '',
  label: '-------Select-------',
};

export const Select: React.FunctionComponent<CustomSelectProps> = (
  props: CustomSelectProps,
) => {
  const { clearable = false, options, placeholder, ...rest } = props;
  const selectOptions = [...(clearable ? [EmptySelection] : []), ...options];
  return (
    <TextField
      {...rest}
      select
      SelectProps={{
        MenuProps: {
          PaperProps: {
            sx: {
              overflowX: 'hidden',
              maxHeight: 200, // Set the maximum height for the menu
              overflowY: 'auto', // Enable vertical scrolling
            },
          },
          sx: {
            zIndex: 1400,
          },
        },
      }}
    >
      {selectOptions.map(option => {
        return (
          <MenuItem
            key={option.value}
            value={option.value}
            disabled={option?.disabled === true}
          >
            {option.label}
          </MenuItem>
        );
      })}
    </TextField>
  );
};

export const DefaultSelect: React.FunctionComponent<CustomSelectProps> = (
  props: CustomSelectProps,
) => {
  const { options, ...rest } = props;
  const defaultOption = { value: 'NA', label: 'Not Applicable' };

  return (
    <TextField
      {...rest}
      select
      SelectProps={{
        MenuProps: {
          PaperProps: {
            sx: {
              overflowX: 'hidden',
              maxHeight: 200, // Set the maximum height for the menu
              overflowY: 'auto', // Enable vertical scrolling
            },
          },
        },
      }}
    >
      <MenuItem key={defaultOption.value} value={defaultOption.value}>
        {defaultOption.label}
      </MenuItem>
      {options.map(option => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const RawSelect: React.FunctionComponent<RawSelectProps> = (
  props: RawSelectProps,
) => {
  const {
    options,
    placeholder,
    fullWidth,
    helperText,
    onChange,
    label,
    error,
    ...rest
  } = props;

  function getDisplayVal(value: string): string {
    const option = options.find(option => option.value === value);
    return option ? (option.label as string) : value;
  }

  return (
    <FormControl error={Boolean(helperText)} fullWidth={fullWidth}>
      <InputLabel id="label" shrink required={props.required}>
        {label}
      </InputLabel>
      <MuiSelect
        {...rest}
        displayEmpty
        labelId="label"
        fullWidth={fullWidth}
        onChange={onChange}
        input={<OutlinedInput label={label} notched={true} />}
        renderValue={selected => {
          if (!(props.value as boolean) && placeholder) {
            return (
              <Typography
                sx={{
                  color: 'rgba(0, 0, 0, 0.42)',
                  fontSize: 'inherit',
                  lineHeight: 'inherit',
                  userSelect: 'none',
                }}
              >
                {getDisplayVal(placeholder)}
              </Typography>
            );
          }

          return getDisplayVal(props.value as string);
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              overflowX: 'hidden',
              maxHeight: 200, // Set the maximum height for the menu
              overflowY: 'auto', // Enable vertical scrolling
            },
          },
          sx: {
            zIndex: 1400,
          },
        }}
      >
        {options.map(option => {
          return (
            <MenuItem
              key={option.value}
              value={option.value}
              disabled={option?.disabled === true}
            >
              {option.label}
            </MenuItem>
          );
        })}
      </MuiSelect>
      {Boolean(error) && Boolean(helperText) && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};
